// Pure expansion-tracker logic, ported from src/pwhlstats/web/expansion.py.
// No DOM / no DB imports, so the Node parity harness (js/test-cli.ts) can run
// it directly and tests/test_expansion_port.py can assert it matches Python.
// expansion.ts adds the DB queries + DOM rendering on top.
//
// Dates: phases/key-dates arrive as ISO-8601 strings carrying the America/
// New_York offset (e.g. "2026-05-28T15:00:00-04:00"), so Date.parse yields the
// correct absolute instant. State helpers take an explicit `nowMs` (epoch ms)
// so the parity test can pin "now"; the page passes Date.now().

// ---------------------------------------------------------------------------
// Constants (mirror web/expansion.py + app.py)
// ---------------------------------------------------------------------------
export const CONTRACT_LABELS: Record<string, string> = {
  signed_2829: "Signed through 2028-29",
  signed_2728: "Signed through 2027-28",
  signed_2627: "Signed through 2026-27",
  expiring: "Expiring",
  expiring_rights: "Expiring (rights held)",
  unknown: "Status TBD",
};

// Move kind -> [display label, css modifier class]. The phase number is
// appended by tags() ("Protected · P1").
export const MOVE_TAGS: Record<string, [string, string]> = {
  protected: ["Protected", "tag-protected"],
  re_signed: ["Re-signed", "tag-resigned"],
  efo: ["EFO", "tag-efo"],
  fpo: ["FPO", "tag-fpo"],
  signed: ["Signed", "tag-signed"],
  selected: ["Selected", "tag-selected"],
  qualified: ["Qualified", "tag-qualified"],
  retired: ["Retiring", "tag-retiring"],
};

export const EXP_CONTRACT_ORDER: Record<string, number> = {
  signed_2829: 0, signed_2728: 1, signed_2627: 2,
  expiring: 3, expiring_rights: 4, unknown: 5,
};
export const EXP_POS_ORDER: Record<string, number> = { G: 0, D: 1, F: 2 };

export const DRAFT_SLOT_ORDER: string[] = [
  "VAN", "SEA", "DET", "SJ", "LV", "HAM", "NY", "TOR", "MIN", "BOS", "OTT", "MTL",
];
export const DRAFT_ROUNDS = 6;

// ---------------------------------------------------------------------------
// Types — plain data mirroring the DB rows / dataclasses
// ---------------------------------------------------------------------------
export interface Move {
  phase: number;
  kind: string;
  to_team: string | null;
  note: string;
  source_url: string;
}
export interface Player {
  name: string;
  position: string;
  origin_team: string;
  contract: string;
  player_id: number | null;
  jersey: string | null;
  ep_id: string | null;
  moves: Move[];
}
export interface Team {
  code: string;
  name: string;
  kind: string; // "existing" | "expansion"
  note: string;
}
export interface KeyDate {
  when: string; // ISO-8601 with offset
  label: string;
  audience: string;
}
export interface Phase {
  number: number;
  name: string;
  start: string; // ISO-8601 with offset
  end: string;
  summary: string;
  dates: KeyDate[];
}
export interface PickTrade {
  round: number;
  slot: number;
  owner: string;
  note: string;
  source_url: string;
}
export interface DraftSelection {
  round: number;
  slot: number;
  player: string;
  source_url: string;
  position: string;
  previous_team: string;
  nationality: string;
}
export interface DraftPick {
  round: number;
  slot: number;
  overall: number;
  original_team: string;
  owner: string;
  via: string;
  is_traded: boolean;
  trade_note: string;
  trade_source: string;
  selection: DraftSelection | null;
}

// ---------------------------------------------------------------------------
// Player helpers (mirror Player properties in expansion.py)
// ---------------------------------------------------------------------------
export function currentTeam(p: Player): string {
  for (let i = p.moves.length - 1; i >= 0; i -= 1) {
    if (p.moves[i]!.to_team) return p.moves[i]!.to_team as string;
  }
  return p.origin_team;
}
export function isDepartedFrom(p: Player, code: string): boolean {
  return p.origin_team === code && currentTeam(p) !== code;
}
export function isRetired(p: Player): boolean {
  return p.moves.some((mv) => mv.kind === "retired");
}
export function contractLabel(contract: string): string {
  return CONTRACT_LABELS[contract] ?? "Status TBD";
}
export function positionGroup(position: string): "G" | "D" | "F" {
  const pos = (position || "").toUpperCase();
  if (pos === "G") return "G";
  if (pos === "D" || pos === "LD" || pos === "RD") return "D";
  return "F";
}
function titleCase(s: string): string {
  // Mirror Python str.title() for the generic move-kind fallback
  // (e.g. "some_kind" -> "Some Kind"). Only used for unknown kinds.
  return s.replace(/\w+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}
export function tags(p: Player): [string, string][] {
  const out: [string, string][] = [];
  for (const mv of p.moves) {
    const known = MOVE_TAGS[mv.kind];
    const [label, css] = known ?? [titleCase(mv.kind.replace(/_/g, " ")), "tag-generic"];
    const suffix = mv.phase ? ` · P${mv.phase}` : "";
    out.push([`${label}${suffix}`, css]);
  }
  if (p.contract.startsWith("signed_") && p.moves.length === 0) {
    out.push(["Signed", "tag-signed"]);
  }
  return out;
}
export function latestMove(p: Player): Move | null {
  return p.moves.length ? p.moves[p.moves.length - 1]! : null;
}

/** Sort key for a roster card, mirroring app._expansion_roster_sort_key.
 *  Returned as a tuple compared field-by-field. */
export function rosterSortKey(p: Player): [number, number, number, string] {
  const isConfirmed = p.moves.length > 0 || p.contract.startsWith("signed_");
  return [
    isConfirmed ? 0 : 1,
    EXP_CONTRACT_ORDER[p.contract] ?? 9,
    EXP_POS_ORDER[positionGroup(p.position)] ?? 9,
    p.name,
  ];
}
export function compareSortKeys(
  a: [number, number, number, string],
  b: [number, number, number, string],
): number {
  for (let i = 0; i < 3; i += 1) {
    if ((a[i] as number) !== (b[i] as number)) return (a[i] as number) - (b[i] as number);
  }
  return a[3] < b[3] ? -1 : a[3] > b[3] ? 1 : 0;
}

export function rosterFor(players: Player[], code: string): Player[] {
  return players.filter((p) => currentTeam(p) === code);
}
export function departedFrom(players: Player[], code: string): Player[] {
  return players.filter((p) => isDepartedFrom(p, code));
}

// ---------------------------------------------------------------------------
// Phase / key-date state (mirror expansion.py)
// ---------------------------------------------------------------------------
export function phaseState(ph: Phase, nowMs: number): "past" | "active" | "upcoming" {
  const start = Date.parse(ph.start), end = Date.parse(ph.end);
  if (nowMs < start) return "upcoming";
  if (nowMs > end) return "past";
  return "active";
}
export function keydateState(kd: KeyDate, nowMs: number): "past" | "upcoming" {
  return nowMs >= Date.parse(kd.when) ? "past" : "upcoming";
}
export function currentPhase(phases: Phase[], nowMs: number): Phase | null {
  for (const ph of phases) {
    if (Date.parse(ph.start) <= nowMs && nowMs <= Date.parse(ph.end)) return ph;
  }
  return null;
}
export function nextPhase(phases: Phase[], nowMs: number): Phase | null {
  for (const ph of phases) {
    if (nowMs < Date.parse(ph.start)) return ph;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Draft board (mirror expansion.py resolved_draft_picks / draft_rounds / …)
// ---------------------------------------------------------------------------
export function draftOverall(rnd: number, slot: number): number {
  return (rnd - 1) * DRAFT_SLOT_ORDER.length + slot;
}
export function resolvedDraftPicks(
  trades: PickTrade[],
  selections: DraftSelection[],
): DraftPick[] {
  const tradeMap = new Map<string, PickTrade>();
  for (const t of trades) tradeMap.set(`${t.round},${t.slot}`, t);
  const selMap = new Map<string, DraftSelection>();
  for (const s of selections) selMap.set(`${s.round},${s.slot}`, s);
  const out: DraftPick[] = [];
  for (let rnd = 1; rnd <= DRAFT_ROUNDS; rnd += 1) {
    DRAFT_SLOT_ORDER.forEach((code, idx) => {
      const slot = idx + 1;
      const tr = tradeMap.get(`${rnd},${slot}`) ?? null;
      out.push({
        round: rnd,
        slot,
        overall: draftOverall(rnd, slot),
        original_team: code,
        owner: tr ? tr.owner : code,
        via: tr ? code : "",
        is_traded: tr !== null,
        trade_note: tr ? tr.note : "",
        trade_source: tr ? tr.source_url : "",
        selection: selMap.get(`${rnd},${slot}`) ?? null,
      });
    });
  }
  return out;
}
export function draftRounds(
  trades: PickTrade[],
  selections: DraftSelection[],
): DraftPick[][] {
  const picks = resolvedDraftPicks(trades, selections);
  const out: DraftPick[][] = [];
  for (let rnd = 1; rnd <= DRAFT_ROUNDS; rnd += 1) {
    out.push(picks.filter((p) => p.round === rnd));
  }
  return out;
}
export function draftPicksFor(
  trades: PickTrade[],
  selections: DraftSelection[],
  code: string,
): DraftPick[] {
  return resolvedDraftPicks(trades, selections).filter(
    (p) => p.owner === code && p.selection !== null,
  );
}
